import { Type } from "@angular/core";
import { ActivatedRoute, Route } from "@angular/router";
import { AuthorizeGuard } from "src/cer-identity/guards/authorize.guard";

import { CerFormAdapter } from "@form/cer-form-adapter";
import { CerFormPanesComponent } from "@form/cer-form-panes/cer-form-panes.component";

import { RoleTypeEnum, VoucherApprovalStatusAllEnum, VoucherMessageChannelTypeEnum, VoucherMessageStatusAllEnum } from "./api";

import { CerAppDocumentationForm } from "src/cer-app/cer-app-documentation/cer-app-documentation.form";
import { CerAppNotificationComponent } from "src/cer-app/cer-app-notifications/cer-app-notification.component";
import { CerAppScheduleTypeForm } from "src/cer-app/cer-app-scheduler/cer-app-schedule-type.form";
import { CerAppSchedulerStatusComponent } from "src/cer-app/cer-app-scheduler/cer-app-scheduler-status.component";

import { HomeComponent } from "@core/home/home.component";
import { UserForm } from "src/app-core/user/user.form";
import { UserApprovalDelegationForm } from "src/app-core/user/user-approval-delegation.form";
import { UserRouteForm } from "src/cer-app/cer-user-route/user-route.form";
import { RouteForm } from "src/cer-app/cer-user-route/route.form";
import { UserRouteViewForm } from "src/cer-app/cer-user-route/user-route-view.form";

import { DataTableForm } from "@core/data/data-table.form";
import { DataNumberSequenceForm } from "@core/data/data-number-sequence.form";
import { DataProcessForm } from "@core/data/data-process.form";
import { DataProcessStageForm } from "@core/data/data-process-stage.form";
import { DataTagForm } from "src/app-core/data/data-tag.form";
import { DataTagTypeForm } from "src/app-core/data/data-tag-type";

import { BlobStorageForm } from "@core/document-lib/blob-storage";
import { BlobStorageTypeForm } from "@core/document-lib/blob-storage-type";
import { DocumentLibForm } from "@core/document-lib/document-lib.form";
import { DocumentLibUserForm } from "@core/document-lib/document-lib-user.form";
import { DocumentLibFinderForm, documentFinderPath } from "@core/document-lib/document-lib-finder.form";

import { MailChannelForm } from "@core/mail/mail-channel.form";
import { MailTemplateComponent } from "@core/mail/mail-template/mail-template.component";

import { FormRecogizerForm } from "@core/form-recogizer/form-recogizer.form";
import { FormRecogizerDocumentForm } from "@core/form-recogizer/form-recogizer-document.form";

import { ExchangeMsgQueueForm } from "@core/exchange/exchange-msg-queue.form";
import { ExchangeMsgForm } from "@core/exchange/exchange-msg.form";
import { ExchangeTypeForm } from "@core/exchange/exchange-type.form";
import { ExchangePartyForm } from "@core/exchange/exchange-party.form";
import { ExchangeTypeSubscriberForm } from "@core/exchange/exchange-type-subscriber.form";

import { DataJobForm } from "@core/data-job/data-job.form";
import { DataJobLogForm } from "@core/data-job/data-job-log.form";
import { DataJobTaskLogForm } from "@core/data-job/data-job-task-log.form";
import { DataJobTaskLogErrorForm } from "@core/data-job/data-job-task-log-error.form";

import { ForfatterComponent } from "./contract/forfatter/forfatter.component";
import { ContractLegacyForm } from "./contract/contract-legacy/contract-legacy.form";
import { AfregningsTabelLegacyForm } from "./contract/contract-legacy/afregningstabel-legacy.form";

import { CurrencyForm } from "./financial/currency.form";
import { TaxCodeForm } from "./financial/tax-code.form";
import { PaymentTermForm } from "./financial/payment-term.form";
import { FinanceProjectTransForm } from "./financial/project/finance-project-trans.form";
import { FinanceLedgerAccountForm } from "./financial/project/finance-ledger-account.form";

import { BankAccountForm } from "./bank/bank-account.form";
import { BankCreditCardForm } from "./bank/bank-credit-card.form";
import { BankAccountSourceForm } from "./bank/bank-account-source.form";
import { BankAccountTransactionSourceForm } from "./bank/transactions/bank-account-transaction-source.form";
import { BankAccountTransactionSourceGroupForm } from "./bank/transactions/bank-account-transaction-source-group.form";
import { BankAccountTransactionSettlementRuleForm } from "./bank/settlement/bank-account-settlement-rule.form";
import { BankAccountTransactionBatchForm } from "./bank/transactions/bank-account-transaction-batch.form";
import { BankAccountSettlementComponent } from "./bank/settlement/bank-account-settlement.component";
import { BankAccountTransferSetupForm } from "./bank/paymernt/bank-account-transfer-setup-form";
import { BankPaymentJournalVendorForm } from "./bank/paymernt/bank-payment-journal-vendor.form";
import { BankVendorForm } from "./bank/paymernt/bank-vendor.form";

import { VendorForm } from "./financial/vendor/vendor.form";
import { VendorViewForm } from "./financial/vendor/vendor-view.form";
import { VendorGroupForm } from "./financial/vendor/vendor-group.form";
import { VendorTaxGroupForm } from "./financial/vendor/vendor-tax-group.form";
import { VendorTransTypeForm } from "./financial/vendor/vendor-trans-type.form";
import { VendorTransForm } from "./financial/vendor/vendor-trans.form";

import { ProductFinanceStatusForm } from "./product/forms/product-finance-status.form";
import { ProductBOMForm } from "./product/forms/product-bom.form";
import { ProductTemplateForm } from "./product/forms/product-template.form";
import { ProductSeriesForm } from "./product/forms/product-series.form";
import { ProductFormForm } from "./product/forms/product-form.form";
import { ProductFormDetailForm } from "./product/forms/product-form-detail.form";
import { ProductMarketAvailabilityForm } from "./product/forms/product-market-availability.form";
import { ProductCategoryForm } from "./product/forms/product-category.form";
import { ProductGroupForm } from "./product/forms/product-group.form";
import { ProductPoolForm } from "./product/forms/product-pool.form";
import { ProductDistributorForm } from "./product/forms/product-distributor.form";
import { ProductPublishingStatusForm } from "./product/forms/product-publishing-status.form";
import { ProductStatusForm } from "./product/forms/product-status.form";
import { ProductSupplierForm } from "./product/forms/product-supplier.form";
import { ProductThemeForm } from "./product/forms/product-theme.form";
import { ProductTypeForm } from "./product/forms/product-type.form";
import { ProductBudgetCategoryForm } from "./product/forms/product-budget-category.form";
import { ProductComponent } from "./product/product.component";
import { ProductListForm } from "./product/forms/product-list.form";

import { RefDataProductForm } from "./product/forms/refdata-product.form";
import { RefDataProductInboundCurrentForm } from "./product/forms/refdata-product-inbound-current.form";
import { RefDataProductInboundContributorCurrentForm } from "./product/forms/refdata-product-inbound-contributor-current.form";
import { RefDataProductInboundForm } from "./product/forms/refdata-product-inbound.form";
import { RefDataProductInboundContributorForm } from "./product/forms/refdata-product-inbound-contributor.form";

import { ContractComponent } from "./contract/contract.component";
import { ContractSaleForm } from "./contract/forms/contract-sale.form";
import { ContractTypeForm } from "./contract/forms/contract-type.form";

import { RightsPeriodForm } from "./rights/rights-period.form";
import { RightsApprovalComponent } from "./rights/rights-approval/rights-approval.component";

import { RoyaltyHistoryForm } from "./royalty/royalty-history.form";
import { RoyaltyStatusForm } from "./royalty/royalty-status.form";
import { RoyaltyPeriodForm } from "./royalty/royalty-period/royalty-period.form";
import { RoyaltyForlagComponent } from "./royalty/royalty-forlag.component";
import { RoyaltyApproveComponent } from "./royalty/royalty-approve.component";

import { CountryForm } from "./party/country.form";
import { LanguageForm } from "./party/language.form";
import { PartyForm, isReviewerParam } from "./party/party.form";
import { PartyRoleForm } from "./party/party-role.form";
import { ProjectTaskForm } from "./project/project-task.form";
import { ProjectTaskStatusForm } from "./project/project-task-status.form";
import { ProjectLevelForm } from "./project/project-level.form";
import { ProjectTaskTypeForm } from "./project/project-task-type.form";
import { TeamForm } from "./project/team.form";

import { SalesOrderTypeForm } from "./sales/forms/sales-order-type.form";
import { SalesPaymentTypeForm } from "./sales/forms/sales-payment-type.form";
import { SalesOrderLineTypeForm } from "./sales/forms/sales-order-line-type.form";
import { SalesChannelForm } from "./sales/forms/sales-channel.form";
import { SalesOrderForm } from "./sales/sales-order.form";
import { SalesOrderCustComponent } from "./sales/sales-order-cust.component";
import { SalesChannelOrderForm } from "./sales/forms/sales-channel-order.form";
import { CustInvoiceForm } from "./sales/forms/cust-invoice.form";
import { RefDataNemHandelsRegisterForm } from "./masterdata/forms/ref-data-nem-handels-register.form";

import { WarehouseForm } from "./warehousing/forms/warehouse.form";
import { WarehouseCarrierForm } from "./warehousing/forms/warehouse-carrier.form";
import { WarehouseOutboundOrderForm } from "./warehousing/warehouse-outbound-order.form";
import { WarehouseOutboundOrderTypeForm } from "./warehousing/forms/warehouse-outbound-order-type.form";
import { WarehouseOnhandStatusForm } from "./warehousing/forms/warehouse-onhand-status.form";
import { WarehousePurchLineForm } from "./warehousing/forms/warehouse-purch-line.form";
import { WarehouseOutboundBackOrderOnHandStatusForm } from "./warehousing/forms/warehouse-outbound-backorder-status.form";
import { WarehouseOutboundConfirmTransForm } from "./warehousing/forms/warehouse-outbound-confirm-trans.form";
import { WarehouseOutboundReserveTransForm } from "./warehousing/forms/warehouse-outbound-reserve-trans.form";
import { WarehouseOutboundDispatchTransForm } from "./warehousing/forms/warehouse-outbound-dispatch-trans.form";
import { WarehouseOutboundCompleteTransForm } from "./warehousing/forms/warehouse-outbound-complete-trans.form";
import { WarehouseOnhandCostJournalForm } from "./warehousing/forms/warehouse-onhand-cost-journal.form";
import { WarehouseOnhandCostJournalLineForm } from "./warehousing/forms/warehouse-onhand-cost-journal-line.form";
import { WarehouseOnhandCostPriceGroupAgeForm } from "./warehousing/forms/warehouse-onhand-cost-price-group-age.form";

import { VoucherCategoryFormAdapter } from "./voucher/voucher-category.form";
import { VoucherValidationStatusForm } from "./voucher/voucher-validation-status.form";
import { VoucherMessageChannelForm } from "./voucher/voucher-message-channel.form";
import { VoucherMessageChannelUserForm } from "./voucher/voucher-message-channel-user.form";
import { VoucherMessageChannelNextVoucherForm } from "./voucher/voucher-message-channel-next-voucher.form";
import { VoucherMessageMailVendorHistoryForm } from "./voucher/voucher-message-mail-vendor-history.form";
import { VoucherApprovalForm } from "./voucher/voucher-policy/voucher-approval.form";
import { VoucherApprovalPolicyForm } from "./voucher/voucher-policy/voucher-approval-policy.form";
import { VoucherMessageListComponent } from "./voucher/vouchermessage-list/vouchermessage-list.component";
import { VoucherListComponent } from "./voucher/voucher-list/voucher-list.component";
import { VoucherRequestForm } from "./voucher/voucher-request.form";
import { VoucherLookupComponent } from "./voucher/voucher-lookup/voucher-lookup.component";
import { VoucherCreateMobileComponent } from "./voucher/voucher-create-mobile/voucher-create-mobile.component";
import { VoucherPostingForm } from "./voucher/voucher-posting.form";
import { VoucherPostingProjectForm } from "./voucher/voucher-posting-project.form";
import { VoucherPostingVendorForm } from "./voucher/voucher-posting-vendor.form";

// Module navigation
export const GeneratedNavigationSettings: CerNavigationSetting[] = [

  { path: 'masterdata', title: 'Stamdata', navShortcut: 'S' ,roles: [RoleTypeEnum.BaseData, RoleTypeEnum.BaseDataBasic] },

  { path: 'rights', title: 'Forfatterafregning', navShortcut: 'A', roles: [RoleTypeEnum.RightsBasic, RoleTypeEnum.RightsAdmin] },
  { path: 'royalty', title: 'Royaltyafregning', navShortcut: 'R', roles: [RoleTypeEnum.RoyaltyApprover] },

  { path: 'voucher-my', title: 'Bilagsgodkendelse', navShortcut: 'B', roles: [RoleTypeEnum.VoucherExpenseCreate, RoleTypeEnum.VoucherApproval] },

  { path: 'sales', title: 'Salg', navShortcut: 'S', roles: [RoleTypeEnum.Webshop] },
  { path: 'sales/capture', title: 'Indløsning', navShortcut: 'SI', roles: [RoleTypeEnum.Webshop] },
  { path: 'sales/credit', title: 'Kredit', navShortcut: 'SK', roles: [RoleTypeEnum.Webshop] },

  { path: 'warehouse-outbound', title: 'Forsendelser', navShortcut: 'F', roles: [RoleTypeEnum.Webshop] },
  { path: 'warehouse', title: 'Lager', navShortcut: 'L', roles: [RoleTypeEnum.Warehouse, RoleTypeEnum.WarehouseProductList] },

  { path: 'project', title: 'Opgaver', navShortcut: 'O', roles: [RoleTypeEnum.ProjectBasic, RoleTypeEnum.ProjectStructure, RoleTypeEnum.ProjectTask, RoleTypeEnum.ProjectAdmin] },
  { path: 'project/node', title: 'Opgavestruktur', navShortcut: 'OS', roles: [RoleTypeEnum.ProjectStructure, RoleTypeEnum.ProjectAdmin] },

  { path: 'document-lib', title: 'Dokumentarkiver', navShortcut: 'D' },

  { path: 'finance', title: 'Økonomi', navShortcut: 'Ø', roles: [RoleTypeEnum.FinanceAdmin, RoleTypeEnum.VoucherVendorAdmin, RoleTypeEnum.VoucherExpenseAdmin, RoleTypeEnum.VoucherManualAdmin, RoleTypeEnum.AccountingBankPayment] },

  { path: 'finance/voucher-vendor', title: 'Kreditorfakturaer', navShortcut: 'ØK', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'finance/voucher-vendor/message', title: 'Beskeder', navShortcut: 'ØKM', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'finance/voucher-vendor/voucher', title: 'Bilag', navShortcut: 'ØKB', roles: [RoleTypeEnum.VoucherVendorAdmin] },

  { path: 'finance/voucher-expense', title: 'Udgiftsbilag', navShortcut: 'ØU', roles: [RoleTypeEnum.VoucherExpenseAdmin] },
  { path: 'finance/voucher-expense/message', title: 'Beskeder', navShortcut: 'ØUM', roles: [RoleTypeEnum.VoucherExpenseAdmin] },
  { path: 'finance/voucher-expense/voucher', title: 'Bilag', navShortcut: 'ØUB', roles: [RoleTypeEnum.VoucherExpenseAdmin] },
  { path: 'finance/voucher-expense/bank', title: 'Bank', navShortcut: 'ØUA', roles: [RoleTypeEnum.VoucherExpenseAdmin] },

  { path: 'finance/voucher-manual-voucher', title: 'Manuelle bilag', navShortcut: 'ØM', roles: [RoleTypeEnum.VoucherManualAdmin] },

  { path: 'finance/vendor', title: 'Kreditorer', navShortcut: 'ØR', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'finance/payment', title: 'Betalinger',  navShortcut: 'ØB', roles: [RoleTypeEnum.AccountingBankPayment] },

  { path: 'finance/product', title: 'Titler', navShortcut: 'ØT', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'finance/warehouse', title: 'Lager',  navShortcut: 'ØL', roles: [RoleTypeEnum.FinanceAdmin] },

  { path: 'finance/trans', title: 'Transaktioner',  navShortcut: 'ØR', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'finance/trans/voucher', title: 'Bilag',  navShortcut: 'ØRB', roles: [RoleTypeEnum.VoucherVendorAdmin] },

  { path: 'admin', title: 'Opsætning', navShortcut: 'O', roles: [RoleTypeEnum.GlobalAdmin, RoleTypeEnum.TennantAdmin, RoleTypeEnum.VoucherExpenseAdmin, RoleTypeEnum.VoucherVendorAdmin, RoleTypeEnum.RoyaltyAdmin], position: 'last' },
  { path: 'admin/system', title: 'System', navShortcut: 'OS' },
  { path: 'admin/system/exchange', title: 'Dataudveksling', navShortcut: 'OSU', roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/system/exchange/job', title: 'Datajobs', navShortcut: 'OSUJ', roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/system/job', title: 'Jobafvikling', navShortcut: 'OSJ', roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/system/form-recognizer', title: 'Formulargenkendelse', navShortcut: 'OSF', roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.VoucherExpenseAdmin, RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'admin/system/document-lib', title: 'Dokumentarkiver', navShortcut: 'OSD', roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/system/mail', title: 'Mail', navShortcut: 'OSM', roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/user', title: 'Brugere', navShortcut: 'OB', roles: [RoleTypeEnum.GlobalAdmin, RoleTypeEnum.TennantAdmin] },
  { path: 'admin/warehouse', title: 'Forsendelser', navShortcut: 'OF', roles: [RoleTypeEnum.VoucherExpenseAdmin] },
  { path: 'admin/masterdata', title: 'Stamdata', navShortcut: 'OS' },
  { path: 'admin/masterdata/data', title: 'Generelt', navShortcut: 'OSG', roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.BaseData, RoleTypeEnum.ProjectTask] },
  { path: 'admin/masterdata/party', title: 'Adresser', navShortcut: 'OSA', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'admin/product', title: 'Titler', navShortcut: 'OT' , roles: [RoleTypeEnum.TennantAdmin] },
  { path: 'admin/product/setup', title: 'Opsætning', navShortcut: 'OTO' },
  { path: 'admin/product/onix', title: 'Integration', navShortcut: 'OTI'  },
  { path: 'admin/product/contract', title: 'Kontrakter', navShortcut: 'OTK'  },
  { path: 'admin/product/rights', title: 'Forfatterafregning', navShortcut: 'OTA' },
  { path: 'admin/product/royalty', title: 'Royalty', navShortcut: 'OTR' },
  { path: 'admin/project', title: 'Opgaver', navShortcut: 'OO', roles: [RoleTypeEnum.ProjectAdmin] },
  { path: 'admin/sales', title: 'Salg', navShortcut: 'OS', roles: [RoleTypeEnum.Webshop] },
  { path: 'admin/finance', title: 'Økonomi', navShortcut: 'OØ' },
  { path: 'admin/finance/ledger', title: 'Bogføring', navShortcut: 'OØO' },
  { path: 'admin/finance/vendor', title: 'Kreditor', navShortcut: 'OØR' },
  { path: 'admin/finance/warehouse', title: 'Lager', navShortcut: 'OØL' },
  { path: 'admin/finance/voucher', title: 'Bilag', navShortcut: 'OØB', roles: [RoleTypeEnum.VoucherVendorAdmin] },
  { path: 'admin/finance/bank', title: 'Bank', navShortcut: 'OØA', roles: [RoleTypeEnum.VoucherExpenseAdmin] },
];

// Routes
export const GeneratedRouteSettings: CerRouteSetting[] = [
  {
    formType: HomeComponent,
    path: 'home',
    pathMatch: 'full',
    roles: [],
    title: 'Forside',
    navHide: true
  },
  {
    entity: 'Notification',
    formType: CerAppNotificationComponent,
    path: 'notification',
    roles: [],
    title: 'Notifikation',
    navHide: true
  },
  {
    entity: 'ExchangeMsgQueue',
    formType: ExchangeMsgQueueForm,
    path: 'exchange/queue',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling forslag',
    navTitle: 'Forslag',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    formType: ForfatterComponent,
    path: 'exchange/queue-party',
    data: { fromExchange: true },
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling forslag forfatter',
    navTitle: 'Forslag forfatter',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    formType: ExchangeMsgForm,
    path: 'exchange/waiting',
    data: { waiting: true },
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling venter',
    navTitle: 'Venter',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    formType: ExchangeMsgForm,
    path: 'exchange/failed',
    data: { failed: true },
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling fejl',
    navTitle: 'Fejl',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'ExchangeMsg',
    formType: ExchangeMsgForm,
    path: 'exchange/all',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling alle status',
    navTitle: 'Alle status',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'ExchangeType',
    formType: ExchangeTypeForm,
    path: 'exchange/type',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling typer',
    navTitle: 'Typer',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'ExchangeTypeSubscriber',
    formType: ExchangeTypeSubscriberForm,
    path: 'exchange/type-subscription',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling abonnementer',
    navTitle: 'Abonnementer',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'ExchangeParty',
    formType: ExchangePartyForm,
    path: 'exchange/party',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataudveksling parter',
    navTitle: 'Parter',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'DataJob',
    formType: DataJobForm,
    path: 'job/data-job',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Datajobs',
    navAdmin: true,
    navPrefix: 'system/exchange'
  },
  {
    entity: 'DataJobLog',
    formType: DataJobLogForm,
    path: 'job/data-job-log',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Datajob logs',
    navAdmin: true,
    navPrefix: 'system/exchange'
  },
  {
    entity: 'DataJobTaskLog',
    formType: DataJobTaskLogForm,
    path: 'job/data-job-task-log',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Datajob opgavelog',
    navAdmin: true,
    navPrefix: 'system/exchange'
  },
  {
    entity: 'DataJobTaskLogError',
    formType: DataJobTaskLogErrorForm,
    path: 'job/data-job-task-log-error',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Datajob opgavefejl',
    navAdmin: true,
    navPrefix: 'system/exchange'
  },
  {
    formType: CerAppSchedulerStatusComponent,
    path: 'job/status',
    roles: [RoleTypeEnum.GlobalAdmin],
    title: 'Jobafvikling status',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'ScheduleType',
    formType: CerAppScheduleTypeForm,
    path: 'job/schedule',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Jobafvikling tidsplaner',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'MailChannel',
    formType: MailChannelForm,
    path: 'mail/message-channel-out',
    roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.TennantAdmin],
    title: 'Mail udgående kanaler',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'VoucherMessageChannel',
    formType: VoucherMessageChannelForm,
    path: 'mail/message-channel-in',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Mail indgående kanaler',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'MailTemplate',
    formType: MailTemplateComponent,
    path: 'mail/template',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Mail skabeloner',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entities: [{ name: 'User', keys: ['id', 'shortName'] }],
    formType: UserForm,
    params: ['simpleMode'],
    path: 'user/user-list',
    title: 'Medarbejdere',
    roles: [RoleTypeEnum.BaseData],
    navAdmin: true
  },
  {
    formType: UserForm,
    path: 'user/user',
    title: 'Brugere',
    roles: [RoleTypeEnum.GlobalAdmin],
    navAdmin: true
  },
  {
    entity: 'UserApprovalDelegation',
    formType: UserApprovalDelegationForm,
    path: 'user/approval-delegation',
    navTitle: 'Stedfortrædere',
    title: 'Godkender stedfortrædere',
    roles: [RoleTypeEnum.GlobalAdmin, RoleTypeEnum.TennantAdmin],
    navAdmin: true
  },
  {
    entity: 'AppUserRoute',
    formType: UserRouteForm,
    path: 'user/route',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Brugeropsætning sider',
    navAdmin: true
  },
  {
    entity: 'AppRoute',
    formType: RouteForm,
    path: 'user/route',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Sider brugeropsætning',
    navAdmin: true
  },
  {
    entity: 'AppUserRouteView',
    formType: UserRouteViewForm,
    path: 'user/route-view',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Visninger opsætning',
    navAdmin: true,
  },
  {
    entity: 'DataNumberSequence',
    formType: DataNumberSequenceForm,
    path: 'data/sequence',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Nummerserier',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'DataProcess',
    formType: DataProcessForm,
    path: 'data/process',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataprocesser',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'DataProcessStage',
    formType: DataProcessStageForm,
    path: 'data/process-stage',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dataproces stadier',
    navHide: true,
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'DataTable',
    formType: DataTableForm,
    path: 'data/table',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Tabeller',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'DataTagType',
    formType: DataTagTypeForm,
    path: 'data/tag-type',
    roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.BaseData, RoleTypeEnum.ProjectTask],
    title: 'Kategorier',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'DataTag',
    formType: DataTagForm,
    path: 'data/tag',
    roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.BaseData, RoleTypeEnum.ProjectTask],
    title: 'Kategoritilknytninger',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    formType: DocumentLibFinderForm,
    path: documentFinderPath,
    roles: [],
    title: 'Dokumenter',
    personizationDisabled: true,
    navPath: documentFinderPath,
    navHide: true
  },
  {
    entity: 'DocumentLib',
    formType: DocumentLibForm,
    path: 'document-lib/list',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dokumentarkiver',
    titleSingular: 'Dokumentarkiv',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'DocumentLibUser',
    formType: DocumentLibUserForm,
    path: 'document-lib/user',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Dokumentarkiv brugere',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'BlobStorage',
    formType: BlobStorageForm,
    path: 'document-lib/storage',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Documentlagring',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'BlobStorageType',
    formType: BlobStorageTypeForm,
    path: 'document-lib/storage-type',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Documentlagring typer',
    titleSingular: 'Documentlagring type',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'FormRecognizer',
    formType: FormRecogizerForm,
    path: 'form-recognizer/recognizer',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Formular opsætning',
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'FormRecognizerDocument',
    path: 'form-recognizer/document',
    title: 'Dokument historik',
    formType: FormRecogizerDocumentForm,
    roles: [RoleTypeEnum.TennantAdmin, RoleTypeEnum.VoucherVendorAdmin],
    navAdmin: true,
    navPrefix: 'system'
  },
  {
    entity: 'Country',
    formType: CountryForm,
    path: 'party/country',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Lande',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entity: 'Language',
    formType: LanguageForm,
    path: 'party/language',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Sprog',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    entities: [{ name: 'Forfatter', keys: ['id', 'fid'] }],
    formType: ForfatterComponent,
    path: 'contract-party-list',
    roles: [RoleTypeEnum.BaseData],
    title: 'Forfattere',
    navPrefix: 'masterdata'
  },
  {
    entities: [
      { name: 'RefDataProductLookup', keys: ['id', 'num', 'projectNum', 'projectLegacyId'] },
      { name: 'RefDataProduct', keys: ['id', 'num', 'projectNum', 'projectLegacyId'] }
    ],
    formType: RefDataProductForm,
    path: 'product',
    roles: [RoleTypeEnum.BaseData, RoleTypeEnum.BaseDataBasic],
    title: 'Titler',
    titleSingular: 'Titel',
    navPrefix: 'masterdata'
  },
  {
    entities: [{ name: 'ContractLegacy', keys: ['id', 'hKontraktStr'] }],
    formType: ContractLegacyForm,
    path: 'contract-legacy',
    roles: [RoleTypeEnum.BaseData],
    title: 'Kontrakter',
    titleSingular: 'Kontrakt',
    navPrefix: 'masterdata'
  },
  {
    entities: [{ name: 'ContractSale', keys: ['id' ] }],
    formType: ContractSaleForm,
    path: 'contract-sale',
    roles: [RoleTypeEnum.BaseData],
    title: 'Kontrakter solgte',
    titleSingular: 'Kontrakt solgt',
    navPrefix: 'masterdata'
  },
  
  {
    entity: 'ProductBOM',
    formType: ProductBOMForm,
    path: 'product-bom',
    roles: [RoleTypeEnum.BaseData],
    title: 'Bundles',
    navPrefix: 'masterdata'
  },
  {
    entity: 'RefDataProductInboundCurrent',
    formType: RefDataProductInboundCurrentForm,
    path: 'product-inbound',
    roles: [RoleTypeEnum.BaseData],
    title: 'Titler indkommende',
    navPrefix: 'product/onix',
    navAdmin: true
  },
  {
    entity: 'RefDataProductInbound',
    formType: RefDataProductInboundForm,
    path: 'product-inbound-history',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Titler indkommende historik',
    navPrefix: 'product/onix',
    navAdmin: true
  },
  {
    entity: 'RefDataProductInboundContributorCurrent',
    formType: RefDataProductInboundContributorCurrentForm,
    path: 'product-inbound-contributor',
    roles: [RoleTypeEnum.BaseData],
    title: 'Bidragsydere indkommende',
    navPrefix: 'product/onix',
    navAdmin: true
  },
  {
    entity: 'RefDataProductInboundContributor',
    formType: RefDataProductInboundContributorForm,
    path: 'product-inbound-contributor-history',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Bidragsydere indkommende historik',
    navPrefix: 'product/onix',
    navAdmin: true
  },
  {
    entities: [{ name: 'Product', keys: ['id', 'num', 'projectNum'] }],
    formType: ProductComponent,
    path: 'product-edit',
    roles: [/*RoleTypeEnum.BaseData,*/ RoleTypeEnum.TennantAdmin],
    title: 'Titler (rediger)',
    titleSingular: 'Titel (rediger)',
    navPrefix: 'masterdata'
  },
  {
    entity: 'Contract',
    formType: ContractComponent,
    path: 'contract-list',
    roles: [/*RoleTypeEnum.BaseData,*/ RoleTypeEnum.TennantAdmin],
    title: 'Kontrakter (rediger)',
    navPrefix: 'masterdata'
  },
  {
    entity: 'Party',
    formType: PartyForm,
    path: 'party',
    roles: [RoleTypeEnum.BaseData],
    title: 'Kontakter',
    navPrefix: 'masterdata'
  },
  {
    entity: 'ProductGroup',
    formType: ProductGroupForm,
    path: 'product/setup/group',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Grupper',
    titleSingular: 'Gruppe',
    navAdmin: true
  },
  {
    entity: 'ProductAvailability',
    formType: ProductMarketAvailabilityForm,
    path: 'product/setup/availability',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Tilgængelighed',
    navAdmin: true
  },
  {
    entity: 'ProductPublishingStatus',
    formType: ProductPublishingStatusForm,
    path: 'product/setup/publishing-status',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Publiseringsstatus',
    navAdmin: true
  },
  {
    entity: 'ProductTemplate',
    formType: ProductTemplateForm,
    path: 'product/setup/template',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Skabeloner',
    titleSingular: 'Skabelone',
    navAdmin: true
  },
  {
    entity: 'ProductFormat',
    formType: ProductFormForm,
    path: 'product/setup/format',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Formater',
    navAdmin: true
  },
  {
    entity: 'ProductFormatDetail',
    formType: ProductFormDetailForm,
    path: 'product/setup/format-detail',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Formater, detaljer',
    navAdmin: true
  },
  {
    entity: 'ProductPool',
    formType: ProductPoolForm,
    path: 'product/setup/pool',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Printpuljer',
    navAdmin: true
  },
  {
    entity: 'ProductStatus',
    formType: ProductStatusForm,
    path: 'product/setup/status',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Udgivelsesstatus',
    navTitle: 'Status',
    navAdmin: true
  },
  {
    entity: 'ProductTheme',
    formType: ProductThemeForm,
    path: 'product/setup/theme',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Temakoder',
    navAdmin: true
  },
  {
    entity: 'ProductType',
    formType: ProductTypeForm,
    path: 'product/setup/type',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Typer',
    navAdmin: true
  },
  {
    entity: 'ProductBudgetCategory',
    formType: ProductBudgetCategoryForm,
    path: 'contract/budget-category',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Udgivelse budgetarter',
    navTitle: 'Budgetarter',
    navAdmin: true,
    navPrefix: 'product'
  },
  {
    entity: 'ProductCategory',
    formType: ProductCategoryForm,
    path: 'product/setup/category',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Varegrupper',
    titleSingular: 'Varegruppe',
    navAdmin: true
  },
  {
    entity: 'ProductDistributor',
    formType: ProductDistributorForm,
    path: 'product/setup/distributor',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Ekspedition',
    navAdmin: true
  },
  {
    entity: 'ProductSupplier',
    formType: ProductSupplierForm,
    path: 'product/setup/supplier',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Trykkerier',
    titleSingular: 'Trykkeri',
    navAdmin: true
  },
  {
    entity: 'ProductSeries',
    formType: ProductSeriesForm,
    path: 'product/setup/series',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Serier',
    navAdmin: true
  },
  {
    entity: 'ContractType',
    formType: ContractTypeForm,
    path: 'contract/type',
    title: 'Kontrakttyper',
    roles: [RoleTypeEnum.TennantAdmin],
    navAdmin: true,
    navPrefix: 'product'
  },
  {
    entity: 'PartyRole',
    formType: PartyRoleForm,
    path: 'party/role',
    roles: [RoleTypeEnum.TennantAdmin],
    title: 'Kontakt roller',
    navAdmin: true,
    navPrefix: 'masterdata'
  },
  {
    formType: RightsApprovalComponent,
    data: { myApprovals: true },
    path: 'rights/approval-my',
    roles: [RoleTypeEnum.RightsBasic],
    title: 'Mine forfatterafregninger',
    navTitle: 'Mine godkendelser'
  },
  {
    entity: 'RightsApproval',
    formType: RightsApprovalComponent,
    data: { myApprovals: false },
    path: 'rights/approval',
    roles: [RoleTypeEnum.RightsBasic, RoleTypeEnum.RightsAdmin],
    title: 'Alle forfatterafregninger',
    navTitle: 'Alle godkendelser'
  },
  {
    entity: 'RightsPeriod',
    formType: RightsPeriodForm,
    path: 'rights/period',
    roles: [RoleTypeEnum.RightsAdmin],
    title: 'Perioder forfatterafregning',
    navAdmin: true,
    navTitle: 'Perioder',
    navPrefix: 'product'
  },
  {
    entity: 'RoyaltyAfregningAktuelView',
    formType: RoyaltyApproveComponent,
    data: { view: 'standard' },
    path: 'royalty/contract-list',
    roles: [RoleTypeEnum.RoyaltyApprover],
    title: 'Royaltyberegning, kontrakter',
    navTitle: 'Kontrakter'
  },
  {
    formType: RoyaltyApproveComponent,
    data: { view: 'editor' },
    path: 'royalty/contract-editor-list',
    roles: [RoleTypeEnum.RoyaltyApprover],
    title: 'Royaltyberegning, kontrakter pr. redaktør',
    navTitle: 'Kontrakter pr. redaktør'
  },
  {
    formType: RoyaltyApproveComponent,
    data: { view: 'publisher' },
    path: 'royalty/contract-publisher-list',
    roles: [RoleTypeEnum.RoyaltyApprover],
    title: 'Royaltyberegning, kontrakter pr. forlag',
    navTitle: 'Kontrakter pr. forlag'
  },
  {
    formType: RoyaltyForlagComponent,
    path: 'royalty/publisher-list',
    roles: [RoleTypeEnum.RoyaltyAdmin],
    title: 'Royaltyberegning, afregning forlag',
    navTitle: 'Afregning forlag'
  },
  {
    entity: 'RoyaltyPeriod',
    formType: RoyaltyPeriodForm,
    path: 'royalty/period',
    roles: [RoleTypeEnum.RoyaltyAdmin],
    title: 'Perioder royalty',
    navAdmin: true,
    navPrefix: 'product',
    navTitle: 'Perioder'
  },
  {
    entity: 'RoyaltyAfregningTransAktuelView',
    formType: RoyaltyStatusForm,
    path: 'royalty/status',
    roles: [RoleTypeEnum.RoyaltyAdmin],
    title: 'Royalty status',
    navTitle: 'Status',
    navAdmin: true,
    navPrefix: 'product'
  },
  {
    entity: 'RoyaltyAfregningTransHistorikView',
    formType: RoyaltyHistoryForm,
    path: 'royalty/history',
    roles: [RoleTypeEnum.RoyaltyAdmin],
    title: 'Royalty historik',
    navTitle: 'Historik',
    navAdmin: true,
    navPrefix: 'product'
  },
  // Voucher admin
  {
    entity: 'VoucherCategory',
    formType: VoucherCategoryFormAdapter,
    path: 'voucher/category',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Bilagskategorier',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherValidationStatus',
    formType: VoucherValidationStatusForm,
    path: 'voucher/validation-status',
    roles: [RoleTypeEnum.VoucherVendorAdmin, RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Valideringsstatus',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherMessageChannelUser',
    formType: VoucherMessageChannelUserForm,
    path: 'voucher/channel-user',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bilag superbrugere',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherMessageChannelNextVoucher',
    formType: VoucherMessageChannelNextVoucherForm,
    path: 'voucher/channel-voucher-num',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bilagsnumre',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherMessageMailVendorHistory',
    formType: VoucherMessageMailVendorHistoryForm,
    path: 'voucher/posting-history',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bilag e-mail afsendere',
    navAdmin: true,
    navPrefix: 'finance'

  },
  {
    entity: 'VoucherApproval',
    formType: VoucherApprovalForm,
    path: 'voucher/approval',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Godkendelser af bilag',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherApprovalPolicy',
    formType: VoucherApprovalPolicyForm,
    path: 'voucher/approval-policy',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Godkendelsespolitikker',
    navAdmin: true,
    navPrefix: 'finance'
  },

  // Vouchers (My)
  {
    formType: VoucherCreateMobileComponent,
    path: 'voucher-my/create',
    roles: [RoleTypeEnum.VoucherExpenseCreate],
    title: 'Indsend bilag',
    navTitle: 'Opret'
  },
  {
    formType: VoucherListComponent,
    data: { approvalStatus: VoucherApprovalStatusAllEnum.Created, myApprovals: true },
    path: 'voucher-my/created',
    roles: [RoleTypeEnum.VoucherExpenseCreate],
    title: 'Mine oprettelser',
    navTitle: 'Oprettede'
  },
  {
    formType: VoucherListComponent,
    data: { approvalStatus: VoucherApprovalStatusAllEnum.Pending, myApprovals: true },
    path: 'voucher-my/pending',
    roles: [RoleTypeEnum.VoucherApproval],
    title: 'Mine "Til godkendelse"',
    navTitle: 'Til godkendelse'
  },
  {
    formType: VoucherListComponent,
    data: { approvalStatus: VoucherApprovalStatusAllEnum.Approved, myApprovals: true },
    path: 'voucher-my/approved',
    roles: [RoleTypeEnum.VoucherApproval],
    title: 'Mine godkendte',
    navTitle: 'Godkendte'

  },
  {
    formType: VoucherListComponent,
    data: { approvalStatus: VoucherApprovalStatusAllEnum.OnHold, myApprovals: true },
    path: 'voucher-my/onhold',
    roles: [RoleTypeEnum.VoucherApproval],
    title: 'Mine afventende godkendelser',
    navTitle: 'Afventer'
  },
  {
    formType: VoucherListComponent,
    data: { approvalStatus: VoucherApprovalStatusAllEnum.All, myApprovals: true },
    path: 'voucher-my/all',
    roles: [RoleTypeEnum.VoucherApproval],
    title: 'Alle mine godkendelser',
    navTitle: 'Alle'
  },
  {
    formType: VoucherLookupComponent,
    data: { showChat: true },
    path: 'voucher/chat/:msgid',
    roles: [RoleTypeEnum.VoucherApproval],
    title: 'Vis godkendelse',
    navHide: true
  },
  {
    formType: VoucherLookupComponent,
    path: 'voucher/lookup/:vouchernum',
    title: 'Vis bilag',
    roles: [RoleTypeEnum.VoucherApproval],
    navHide: true
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.Inbox },
    path: 'voucher-vendor/message/inbox',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Indbakke beskeder til fordeling',
    navPrefix: 'finance',
    navTitle: 'Indbakke'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.Voucher },
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    path: 'voucher-vendor/message/voucher',
    title: 'Beskeder sendt til godkendelse',
    navHide: true,
    navPrefix: 'finance',
    navTitle: 'Til godkendelse'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.OnHold },
    path: 'voucher-vendor/message/onhold',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Beskeder der afventer',
    navPrefix: 'finance',
    navTitle: 'Afventer'
  },

  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.AccountStatement },
    path: 'voucher-vendor/message/statement',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Beskeder med kontoudtog',
    navPrefix: 'finance',
    navTitle: 'Kontoudtog'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.Forwarded },
    path: 'voucher-vendor/message/forwarded',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Beskeder der er videresendt',
    navPrefix: 'finance',
    navTitle: 'Videresendt'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.Rejected },
    path: 'voucher-vendor/message/rejected',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Beskeder der er annulleret',
    navPrefix: 'finance',
    navTitle: 'Annulleret'
  },
  {
    entity: 'VoucherMessageView',
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, messageStatus: VoucherMessageStatusAllEnum.All },
    path: 'voucher-vendor/message/all',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Alle beskeder',
    navPrefix: 'finance',
    navTitle: 'Alle'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Created },
    path: 'voucher-vendor/voucher/created',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Oprettede bilag',
    navHide: true,
    navPrefix: 'finance',
    navTitle: 'Oprettede'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Pending },
    path: 'voucher-vendor/voucher/pending',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Til godkendelse',
    navPrefix: 'finance'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.OnHold },
    path: 'voucher-vendor/voucher/onhold',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Afventende godkendelser',
    navPrefix: 'finance',
    navTitle: 'Afventer'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Approved },
    path: 'voucher-vendor/voucher/approved',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Godkendte',
    navPrefix: 'finance'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Accounted },
    path: 'voucher-vendor/voucher/accounted',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Afsluttede godkendelser',
    navPrefix: 'finance',
    navTitle: 'Afsluttede'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Posted },
    path: 'voucher-vendor/voucher/posted',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bogførte godkendelser',
    navPrefix: 'finance',
    navTitle: 'Bogførte'
  },
  {
    entity: 'VoucherView',
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.VendorVoucher, approvalStatus: VoucherApprovalStatusAllEnum.All },
    path: 'voucher-vendor/voucher/all',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Alle godkendelser',
    navPrefix: 'finance',
    navTitle: 'Alle'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.Inbox },
    path: 'voucher-expense/message/inbox',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Indbakke beskeder til fordeling',
    navPrefix: 'finance',
    navTitle: 'Indbakke'
  },
  {
    formType: VoucherMessageListComponent,
    data: { VoucherMessageChannelTypeEnum: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.Voucher },
    path: 'voucher-expense/message/voucher',
    navHide: true,
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Beskeder bilag',
    navPrefix: 'finance',
    navTitle: 'Bilag'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.OnHold },
    path: 'voucher-expense/message/onhold',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Beskeder der afventer',
    navPrefix: 'finance',
    navTitle: 'Afventer'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.AccountStatement },
    path: 'voucher-expense/message/statement',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Beskeder med kontoudtog',
    navPrefix: 'finance',
    navTitle: 'Kontoudtog'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.Forwarded },
    path: 'voucher-expense/message/forwarded',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Beskeder der er videresendt',
    navPrefix: 'finance',
    navTitle: 'Videresendt'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.Rejected },
    path: 'voucher-expense/message/rejected',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Beskeder der er annulleret',
    navPrefix: 'finance',
    navTitle: 'Annulleret'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.All },
    path: 'voucher-expense/message/all',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Alle beskeder',
    navPrefix: 'finance',
    navTitle: 'Alle'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Created },
    path: 'voucher-expense/voucher/created',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Oprettede bilag',
    navPrefix: 'finance',
    navTitle: 'Oprettede'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Pending },
    path: 'voucher-expense/voucher/pending',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Bilag til godkendelse',
    navPrefix: 'finance',
    navTitle: 'Til godkendelse'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.OnHold },
    path: 'voucher-expense/voucher/onhold',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Afventende bilag',
    navPrefix: 'finance',
    navTitle: 'Afventer'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Approved },
    path: 'voucher-expense/voucher/approved',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Godkendte bilag',
    navPrefix: 'finance',
    navTitle: 'Godkendte'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Accounted },
    path: 'voucher-expense/voucher/accounted',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Afsluttede bilag',
    navPrefix: 'finance',
    navTitle: 'Afsluttede'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Posted },
    path: 'voucher-expense/voucher/posted',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Bogførte bilag',
    navPrefix: 'finance',
    navTitle: 'Bogførte'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.All },
    path: 'voucher-expense/voucher/all',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Alle bilag',
    navPrefix: 'finance',
    navTitle: 'Alle'
  },
  {
    formType: VoucherRequestForm,
    path: 'voucher-expense/voucher/request',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Bilagsanmodninger',
    navPrefix: 'finance',
    navTitle: 'Anmodninger'
  },
  {
    formType: VoucherMessageListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ManualVoucher, messageStatus: VoucherMessageStatusAllEnum.All },
    path: 'voucher-manual-message/all',
    roles: [RoleTypeEnum.VoucherManualAdmin],
    title: 'Alle manuelle beskeder',
    navPrefix: 'finance',
    navHide: true
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ManualVoucher, approvalStatus: VoucherApprovalStatusAllEnum.Posted },
    path: 'voucher-manual-voucher/posted',
    roles: [RoleTypeEnum.VoucherManualAdmin],
    title: 'Manuelt bogførte bilag',
    navPrefix: 'finance',
    navTitle: 'Bogførte'
  },
  // Bank
  {
    entity: 'BankAccount',
    formType: BankAccountForm,
    path: 'bank/account',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Bankkonti',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankCreditCard',
    formType: BankCreditCardForm,
    path: 'bank/credit-card',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Kreditkort',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountTransactionSettlementRule',
    formType: BankAccountTransactionSettlementRuleForm,
    path: 'bank/settlement-rule',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Afstemningsregler',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountSource',
    formType: BankAccountSourceForm,
    path: 'bank/account-source',
    title: 'Kilder til bankkonto',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountTransactionSourceGroup',
    formType: BankAccountTransactionSourceGroupForm,
    path: 'bank/settlement-group',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Kilder posteringer',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountTransactionSource',
    formType: BankAccountTransactionSourceForm,
    path: 'bank/transaction-source',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Kilder posteringer',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountTransactionBatch',
    formType: BankAccountTransactionBatchForm,
    path: 'bank/statements',
    navPrefix: 'finance/voucher-expense',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Indlæsninger'
  },
  {
    formType: BankAccountSettlementComponent,
    path: 'bank/settlement',
    navPrefix: 'finance/voucher-expense',
    roles: [RoleTypeEnum.VoucherExpenseBank],
    title: 'Bankafstemning',
    navTitle: 'Afstemning'
  },
  {
    entity: 'BankPaymentJournalVendor',
    formType: BankPaymentJournalVendorForm,
    path: 'payment/payment-journal',
    roles: [RoleTypeEnum.AccountingBankPayment],
    title: 'Kreditorer betalingskladde',
    navPrefix: 'finance'
  },
  {
    entity: 'BankVendor',
    formType: BankVendorForm,
    path: 'payment/bank',
    roles: [RoleTypeEnum.AccountingBankPayment],
    title: 'Kreditorer betaling',
    navPrefix: 'finance'
  },
  {
    entity: 'BankAccountTransferSetup',
    formType: BankAccountTransferSetupForm,
    path: 'bank/transfer-setup',
    roles: [RoleTypeEnum.AccountingBankPayment],
    title: 'Kreditorer betalingskladde',
    navPrefix: 'finance'
  },
  // Sales
  {
    formType: SalesOrderForm,
    params: ['$statusGroup=New'],
    path: 'sales/new',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer (Nye)',
    navTitle: 'Nye'
  },
  {
    formType: SalesOrderForm,
    params: ['$statusGroup=Planning'],
    path: 'sales/planning',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer (Planlægning)',
    navTitle: 'Planlægning'
  },
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=Remain"],
    path: 'sales/remain',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre (Restordrer)',
    navTitle: 'Restordrer'
  },
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=Delivered"],
    path: 'sales/delivered',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre (Leverede)',
    navTitle: 'Leverede'
  },
  // Sales capture
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=CaptureReady"],
    path: 'sales/capture/ready',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer (Klar til indløsning)',
    navTitle: 'Klar til indløsning'
  },
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=Captured"],
    path: 'sales/capture/completed',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer (Indløste)',
    navTitle: 'Indløste'
  },
  {
    path: 'sales/capture/inbox',
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, messageStatus: VoucherMessageStatusAllEnum.Inbox, custInvoice: true },
    formType: VoucherMessageListComponent,
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Faktura beskeder indløsning',
    navTitle: 'Indløsning beskeder'
  },
  {
    formType: VoucherListComponent,
    data: { channelType: VoucherMessageChannelTypeEnum.ExpenseVoucher, approvalStatus: VoucherApprovalStatusAllEnum.All, custInvoice: true },
    path: 'sales/capture/voucher',
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Faktura bilag indløsning',
    navTitle: 'Indløsning bilag'
  },
  {
    formType: CustInvoiceForm,
    params: ["$status=Draft", "$payment=Capture"],
    path: 'sales/capture/draft',
    roles: [RoleTypeEnum.Webshop],
    title: 'Faktura indløsning kladder'
  },
  {
    formType: CustInvoiceForm,
    params: ["$status=Posted", "$payment=Capture"],
    path: 'sales/capture/posted',
    roles: [RoleTypeEnum.Webshop],
    title: 'Faktura indløsning bogførte'
  },
  // Sales credit
  {
    formType: SalesOrderCustComponent,
    params: ["$statusGroup=CreditCustReady"],
    path: 'sales/credit/cust',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre debitor tilknytninger',
    navTitle: 'Tilknyt debitor'
  },
  {
    formType: CustInvoiceForm,
    params: ["$status=Draft", "$payment=Credit"],
    path: 'sales/credit/draft',
    roles: [RoleTypeEnum.Webshop],
    title: 'Kreditfaktura kladder'
  },
  {
    formType: CustInvoiceForm,
    params: ["$status=Posted", "$payment=Credit"],
    path: 'sales/credit/posted',
    roles: [RoleTypeEnum.Webshop],
    title: 'Kreditfaktura bogførte'
  },
  {
    formType: RefDataNemHandelsRegisterForm,
    path: 'sales/credit/nemhandel',
    roles: [RoleTypeEnum.BaseData],
    title: 'NemHandel adresser'
  },
  // Sales completed
  /*{
    path: 'sales/invoiced',
    formType: SalesOrderComponent,
    title: 'Salgsordre (Faktureret)',
    roles: [RoleTypeEnum.Webshop],
    params: ["$statusGroup=Invoiced"]
  },*/
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=Completed"],
    path: 'sales/completed',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre (Afsluttede)',
    navTitle: 'Afsluttede'
  },
  {
    formType: SalesOrderForm,
    params: ["$statusGroup=Cancelled"],
    path: 'sales/cancelled',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre (Annullerede)',
    navTitle: 'Annullerede'
  },
  {
    entity: 'SalesOrder',
    formType: SalesOrderForm,
    path: 'sales/all',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer (Alle)',
    navTitle: 'Alle'
  },
  {
    entity: 'SalesChannel_Order',
    formType: SalesChannelOrderForm,
    path: 'sales/channel-order',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgskanal ordrer',
    navTitle: 'Kanal ordrer'
  },

  // Sales (admin)

  {
    entity: 'SalesChannel',
    formType: SalesChannelForm,
    path: 'sales/channel',
    navAdmin: true,
    roles: [RoleTypeEnum.VoucherExpenseAdmin],
    title: 'Salgsordrer, kanaler'
  },
  {
    entity: 'SalesOrderType',
    formType: SalesOrderTypeForm,
    path: 'sales/order-type',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer, typer',
    navTitle: 'Typer',
    navAdmin: true,
    navPrefix: 'process'
  },
  {
    entity: 'SalesPaymentType',
    formType: SalesPaymentTypeForm,
    path: 'sales/payment-type',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordre, betalingstyper',
    navTitle: 'Betalingstyper',
    navAdmin: true,
  },
  {
    entity: 'SalesOrderLineType',
    formType: SalesOrderLineTypeForm,
    path: 'sales/line-type',
    roles: [RoleTypeEnum.Webshop],
    title: 'Salgsordrer, linjetyper',
    navTitle: 'Linjetyper',
    navAdmin: true,
  },
  // Warehouse
  {
    formType: WarehouseOutboundOrderForm,
    data: { hasDetails: true },
    params: ['$statusGroup=Draft'],
    path: 'warehouse-outbound/draft',
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser (Kladder)',
    navTitle: 'Kladder'
  },
  {
    path: 'warehouse-outbound/released',
    formType: WarehouseOutboundOrderForm,
    params: ['$statusGroup=Released'],
    data: { hasDetails: true },
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser (Bestillinger)',
    navTitle: 'Bestillinger'
  },
  {
    formType: WarehouseOutboundOrderForm,
    params: ['$statusGroup=Remain'],
    data: { hasDetails: true },
    path: 'warehouse-outbound/remain',
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser (Restordrer)',
    navTitle: 'Restordrer'
  },
  {
    formType: WarehouseOutboundOrderForm,
    params: ['$statusGroup=Delivered'],
    data: { hasDetails: true },
    path: 'warehouse-outbound/delivered',
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser (Leverede)',
    navTitle: 'Leverede'
  },
  {
    formType: WarehouseOutboundOrderForm,
    params: ['$statusGroup=Cancelled'],
    data: { hasDetails: true },
    path: 'warehouse-outbound/cancelled',
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser (Annullerede)',
    navTitle: 'Annullerede'
  },
  {
    entity: 'WarehouseOutboundOrder',
    path: 'warehouse-outbound/all',
    formType: WarehouseOutboundOrderForm,
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelser',
    titleSingular: 'Forsendelse',
    navTitle: 'Alle'
  },
  {
    entity: 'ProductList',
    formType: ProductListForm,
    path: 'warehouse/product-list',
    roles: [RoleTypeEnum.Warehouse, RoleTypeEnum.WarehouseProductList],
    title: 'Titellister'
  },
  {
    entities: [{ name: 'WarehouseOnhandStatus', keys: ['product'] }],
    formType: WarehouseOnhandStatusForm,
    params: ['$filterAll'],
    data: { hasDetails: true },
    path: 'warehouse/onhand',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager beholdninger',
    titleSingular: 'Beholdning',
    navTitle: 'Beholdninger'
  },
  {
    entity: 'WarehouseOnhandCostJournalView',
    formType: WarehouseOnhandCostJournalForm,
    path: 'warehouse/cost-journal',
    roles: [RoleTypeEnum.FinanceAdmin],
    title: 'Standard kost kladder',
    navPrefix: 'finance'
  },
  {
    entity: 'WarehouseOnhandCostJournalLineView',
    formType: WarehouseOnhandCostJournalLineForm,
    path: 'warehouse/cost-journal-line',
    roles: [RoleTypeEnum.FinanceAdmin],
    title: 'Kostpris historik',
    navPrefix: 'finance'
  },
  {
    entity: 'WarehouseOnhandCostPriceGroupAge',
    formType: WarehouseOnhandCostPriceGroupAgeForm,
    path: 'warehouse/cost-group-age',
    roles: [RoleTypeEnum.FinanceAdmin],
    title: 'B-kostpriser',
    navPrefix: 'finance'
  },
  {
    entity: 'WarehousePurchLine',
    formType: WarehousePurchLineForm,
    path: 'warehouse/purchase',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager tilgange',
    navTitle: 'Tilgange'
  },
  {
    formType: WarehouseOutboundBackOrderOnHandStatusForm,
    params: ['$groupByWarehouseOrder'],
    data: { hasDetails: true },
    path: 'warehouse/remain',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager restordre status',
    navTitle: 'Restordre status'
  },
  {
    entity: 'WarehouseOutboundBackOrderOnHandProductStatus',
    formType: WarehouseOutboundBackOrderOnHandStatusForm,
    path: 'warehouse/remain-line',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager restordrelinje status',
    navTitle: 'Restordrelinje status'
  },
  {
    formType: WarehouseOutboundConfirmTransForm,
    path: 'warehouse/confirm',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager bekræftelser',
    navTitle: 'Bekræftelser'
  },
  {
    formType: WarehouseOutboundReserveTransForm,
    path: 'warehouse/reserve',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager reservationer',
    navTitle: 'Reservationer'
  },
  {
    formType: WarehouseOutboundDispatchTransForm,
    path: 'warehouse/dispatch',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager afsendte',
    navTitle: 'Afsendte'
  },
  {
    formType: WarehouseOutboundCompleteTransForm,
    path: 'warehouse/complete',
    roles: [RoleTypeEnum.Warehouse],
    title: 'Lager levererede',
    navTitle: 'Leverede'
  },
  {
    entity: 'Warehouse',
    formType: WarehouseForm,
    path: 'warehouse/warehouse',
    roles: [RoleTypeEnum.Webshop],
    title: 'Lagersteder',
    navAdmin: true,
  },
  {
    entity: 'WarehouseOutboundOrderType',
    formType: WarehouseOutboundOrderTypeForm,
    path: 'warehouse/order-type',
    roles: [RoleTypeEnum.Webshop],
    title: 'Forsendelse ordretyper',
    navTitle: 'Ordretyper',
    navAdmin: true,
  },
  {
    entity: 'WarehouseCarrier',
    formType: WarehouseCarrierForm,
    path: 'warehouse/carrier',
    roles: [RoleTypeEnum.Webshop],
    title: 'Transportører',
    navAdmin: true,
  },

  // Finance
  {
    entity: 'Afregningstabel',
    formType: AfregningsTabelLegacyForm,
    path: 'afregningstabel-legacy',
    roles: [RoleTypeEnum.BaseData],
    title: 'Afregningstabel',
    navPrefix: 'finance/product'
  },
  {
    entities: [{ name: 'RefDataProductStat', keys: 'productNum' }],
    formType: RefDataProductForm,
    params: ['stat'],
    path: 'product/stat',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Titelnøgletal',
    navPrefix: 'finance'
  },
  {
    entity: 'RefDataProductStatCategory',
    formType: RefDataProductForm,
    params: ['statCategory'],
    path: 'product/stat-category',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Titelnøgletal art',
    navPrefix: 'finance'
  },
  {
    entity: 'RefDataProductStatPeriod',
    formType: RefDataProductForm,
    params: ['statPeriod'],
    path: 'product/stat-period',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Titelnøgletal perioder',
    navPrefix: 'finance'
  },
  {
    formType: ProductFinanceStatusForm,
    path: 'product/report',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Titel rapportering',
    navPrefix: 'finance'
  },
  {
    entity: 'FinanceLedgerAccount',
    formType: FinanceLedgerAccountForm,
    path: 'ledger/account',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Finans kontoplan',
    titleSingular: 'Finans konto',
    navAdmin: true,
    navPrefix: 'finance'
  },
  /*  {
      entity: 'FinanceLedgerTrans',
      formType: FinanceLedgerTransForm,
      path: 'finance/ledger/trans',
      roles: [RoleTypeEnum.VoucherVendorAdmin],
      title: 'Finans kontoplan', 
      titleSingular: 'Finans konto'
    },*/
  {

    entity: 'FinanceProjectTrans',
    formType: FinanceProjectTransForm,
    path: 'finance/trans/project',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Projekt omkostninger',
    titleSingular: 'Projekt omkostning'
  },
  {
    entities: [{ name: 'Vendor', keys: ['id'] }],
    formType: VendorForm,
    path: 'vendor/list',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditorer (rediger)',
    titleSingular: 'Kreditor (rediger)',
    navPrefix: 'finance'
  },
  {
    entity: 'VendorView',
    formType: VendorViewForm,
    path: 'vendor/all',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditorer',
    titleSingular: 'Kreditor',
    navPrefix: 'finance'
  },
  {
    entity: 'VoucherPostingProject',
    formType: VoucherPostingProjectForm,
    path: 'voucher/posting-project',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Projekt bilag',
    navPrefix: 'finance/trans'
  },
  {
    entity: 'VoucherPostingVendor',
    formType: VoucherPostingVendorForm,
    path: 'voucher/posting-vendor',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditor bilag',
    navPrefix: 'finance/trans'
  },
  {
    entity: 'VoucherPostingView',
    formType: VoucherPostingForm,
    path: 'voucher/posting',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bilag kontering',
    navPrefix: 'finance'
  },
  {
    entity: 'Voucher',
    formType: VoucherListComponent,
    path: 'voucher/list',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Bilag',
    navPrefix: 'finance',
    navTitle: 'Bilag'
  },
  {
    entities: [{ name: 'VendorTrans', keys: ['id', 'voucher'] }],
    formType: VendorTransForm,
    path: 'vendor/trans',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditorposteringer',
    navPrefix: 'finance',
    navTitle: 'Kreditorposter'
  },
  {
    entity: 'VendorTaxGroup',
    formType: VendorTaxGroupForm,
    path: 'vendor/vendor-tax-group',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditor momsgrupper',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'TaxCode',
    formType: TaxCodeForm,
    path: 'ledger/tax-code',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Momskoder',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'Currency',
    formType: CurrencyForm,
    path: 'ledger/currency',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Valuta',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'PaymentTerm',
    formType: PaymentTermForm,
    path: 'ledger/payment-term',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Betalingsbetingelser',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VendorGroup',
    formType: VendorGroupForm,
    path: 'vendor/group',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditorgrupper',
    navAdmin: true,
    navPrefix: 'finance'
  },
  {
    entity: 'VendorTransType',
    formType: VendorTransTypeForm,
    path: 'vendor/trans-type',
    roles: [RoleTypeEnum.VoucherVendorAdmin],
    title: 'Kreditor posttyper',
    navAdmin: true,
    navPrefix: 'finance'
  },
  // Project
  {
    entity: 'Project',
    formType: ProjectTaskForm,
    path: 'project/node/all',
    title: 'Projektstruktur',
    roles: [RoleTypeEnum.ProjectStructure],
    data: { type: 'project' }
  },
  {
    formType: ProjectTaskForm,
    data: { type: 'project' },
    params: ['$filterMy'],
    path: 'project/node/my',
    roles: [RoleTypeEnum.ProjectStructure, RoleTypeEnum.ProjectTask],
    title: 'Mine projekter'
  },
  {
    entity: 'ProjectTask',
    formType: ProjectTaskForm,
    data: { type: 'task' },
    path: 'project/task',
    roles: [RoleTypeEnum.ProjectStructure],
    title: 'Opgaver'
  },
  {
    formType: ProjectTaskForm,
    data: { type: 'task' },
    params: ['$filterMy'],
    path: 'project/task-my',
    roles: [RoleTypeEnum.ProjectBasic],
    title: 'Mine opgaver'
  },
  {
    formType: ProjectTaskForm,
    data: { type: 'task' },
    path: 'project/task-team',
    params: ['$filterMyTeam'],
    roles: [RoleTypeEnum.ProjectBasic],
    title: 'Team opgaver'
  },
  {
    formType: ProjectTaskForm,
    data: { type: 'task' },
    params: ['$filterMy', '$filterStatusOpen'],
    path: 'project/task-my-open',
    roles: [RoleTypeEnum.ProjectBasic],
    title: 'Mine åbne opgaver'
  },
  {
    formType: ProjectTaskForm,
    data: { type: 'task' },
    params: ['$filterMyTeam', '$filterStatusOpen'],
    path: 'project/task-team-open',
    roles: [RoleTypeEnum.ProjectBasic],
    title: 'Team åbne opgaver'
  },
  {
    entity: 'ProjectLevel',
    formType: ProjectLevelForm,
    path: 'project/level',
    roles: [RoleTypeEnum.ProjectAdmin],
    title: 'Projektniveauer',
    navAdmin: true,
  },
  {
    entity: 'ProjectTaskType',
    formType: ProjectTaskTypeForm,
    path: 'project/task-type',
    roles: [RoleTypeEnum.ProjectAdmin],
    title: 'Opgavetyper',
    navAdmin: true,
  },
  {
    entity: 'ProjectTaskStatus',
    formType: ProjectTaskStatusForm,
    path: 'project/task-status',
    roles: [RoleTypeEnum.ProjectAdmin],
    title: 'Opgavestatus',
    navAdmin: true,
  },
  {
    entity: 'Team',
    formType: TeamForm,
    path: 'project/team',
    title: 'Teams',
    roles: [RoleTypeEnum.ProjectAdmin],
    navAdmin: true,
  },
  {
    entity: 'Party',
    formType: PartyForm,
    path: 'project/reviewer',
    roles: [RoleTypeEnum.ProjectTask],
    params: [isReviewerParam],
    title: 'Anmeldere'
  },
  {
    formType: CerAppDocumentationForm,
    path: 'help',
    roles: [RoleTypeEnum.DocumentationView],
    title: 'Hjælp',
    navHide: true,
    personizationDisabled: true
  }
];

export type CerRouteDataEntityNameList = string | string[];

export class CerRouteDataEntity {
  name: string;
  keys?: string | string[];
}

export class CerRouteData {
  public title?: string;
  public titleSingular?: string;
  public entities?: CerRouteDataEntity[];
  public roles?: RoleTypeEnum[];
  public formType?: Type<CerFormAdapter>;
  public params?: string[];
  public personizationDisabled?: boolean;

  public static entitiesFromEntityName(entityList: CerRouteDataEntity[], entity: CerRouteDataEntityNameList): CerRouteDataEntity[] {
    const entities: CerRouteDataEntity[] = entityList ?? [];
    if (Array.isArray(entity)) {
      (entity as string[]).filter(e => e?.length > 0).forEach(name => entityList.push(<CerRouteDataEntity>{ name: name }));
    }
    else if (entity?.length > 0) {
      entities.push(<CerRouteDataEntity>{ name: entity });
    }
    return entities;
  }
}

export class CerRouteDataInfo extends CerRouteData {
  public activatedRoute: ActivatedRoute;
  public url: string;
}

export class CerRouteSetting {
  public entities?: CerRouteDataEntity[];
  public entity?: CerRouteDataEntityNameList;
  public formType: Type<any>;
  public params?: string[];
  public data?: any;
  public path: string;
  public routeDisabled?: boolean;
  public pathMatch?: string;
  public roles: RoleTypeEnum[];
  public title: string;
  public titleSingular?: string;
  public navAdmin?: boolean;
  public navPath?: string;
  public navPrefix?: string;
  public navTitle?: string;
  public navHide?: boolean;
  public navShortcut?: string;
  public tooltip?: string;
  public personizationDisabled?: boolean;
}

export class CerRouteSettingsUtil {
  public static Routes(): Route[] {
    var routeSettings = GeneratedRouteSettings;
    return routeSettings
      .filter(rs => rs.routeDisabled !== true)
      .map(gr => CerRouteSettingsUtil.toRoute(gr));
  }

  public static toRoute(gr: CerRouteSetting): Route {
    const isFormAdapter: boolean = gr.formType.prototype instanceof CerFormAdapter;

    const data: CerRouteData = gr.data ?? {};
    data.title = gr.title,
      data.entities = CerRouteData.entitiesFromEntityName(gr.entities, gr.entity),
      data.roles = gr.roles;
    if (gr.titleSingular) {
      data.titleSingular ??= gr.titleSingular;
    }
    if (gr.params) {
      data.params ??= gr.params;
    }
    if (isFormAdapter) {
      data.formType = <any>gr.formType;
    }
    data.personizationDisabled = gr.personizationDisabled;
    const route = {
      path: gr.path,
      component: isFormAdapter ? CerFormPanesComponent : gr.formType,
      data: data,
      title: gr.title,
      canActivate: [AuthorizeGuard],
      //pathMath: gr.pathMatch
    } as Route;
    return route;
  }
}

export class CerNavigationSetting {
  public path: string;
  public title: string;
  public navShortcut?: string;
  public roles?: RoleTypeEnum[];
  public position?: 'last';
}

export class CerNavigationNode {
  public nodeId: string;
  public nodeText: string;
  public url?: string;
  public navShortcut?: string;
  public roles: number[];
  public rolesNot?: number[];
  public childNodes: CerNavigationNode[];
  public position?: 'last';
}
